/**
 * Redux slice tracking review-pending order counts per master_auth_status.
 *
 * Fetches counts via Algolia multi-facet queries filtered to
 * `agent_order_review_pending` review status. Supports per-key loading
 * and optimistic decrement on review completion.
 * @module reviewPendingCountSlice
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  multiFacetCountsFromAlgolia,
  MultiFacetCountsFromAlgoliaParams,
} from "../../../../shared/api/postCall/multiFacetCountsFromAlgolia";
import { MedicalOrderReviewStatus } from "../../../../shared/enums/orderReviewStatus";
import {
  AlgoliaFacetAttribute,
  MasterAuthStatusValue,
} from "../../../../shared/enums/worklistEnums";

/** State shape for review-pending counts, supporting independent loading per auth status key. */
interface ReviewPendingCountState {
  /** Counts keyed by master_auth_status (e.g. no_auth_required, auth_on_file) */
  countsByMasterAuthStatus: Record<string, number>;
  loadingByMasterAuthStatus: Record<string, boolean>;
  error?: string;
}

const initialState: ReviewPendingCountState = {
  countsByMasterAuthStatus: {},
  loadingByMasterAuthStatus: {},
  error: undefined,
};

export const reviewPendingCountSlice = createSlice({
  name: "reviewPendingCount",
  initialState,
  reducers: {
    setReviewPendingCountByKey: (
      state,
      action: PayloadAction<{ masterAuthStatus: string; count: number }>,
    ) => {
      const { masterAuthStatus, count } = action.payload;
      state.countsByMasterAuthStatus[masterAuthStatus] = count;
      state.loadingByMasterAuthStatus[masterAuthStatus] = false;
      state.error = undefined;
    },
    setLoadingByKey: (
      state,
      action: PayloadAction<{ masterAuthStatus: string; loading: boolean }>,
    ) => {
      const { masterAuthStatus, loading } = action.payload;
      state.loadingByMasterAuthStatus[masterAuthStatus] = loading;
      if (!loading) state.error = undefined;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      Object.keys(state.loadingByMasterAuthStatus).forEach(
        (k) => (state.loadingByMasterAuthStatus[k] = false),
      );
    },
    resetReviewPendingCount: (state) => {
      state.countsByMasterAuthStatus = {};
      state.loadingByMasterAuthStatus = {};
      state.error = undefined;
    },
    decrementReviewPendingCount: (state) => {
      const key = MasterAuthStatusValue.NoAuthRequired;
      const current = state.countsByMasterAuthStatus[key] ?? 0;
      if (current > 0) {
        state.countsByMasterAuthStatus[key] = current - 1;
      }
    },
    decrementReviewPendingCountByKey: (
      state,
      action: PayloadAction<string>,
    ) => {
      const key = action.payload;
      const current = state.countsByMasterAuthStatus[key] ?? 0;
      if (current > 0) {
        state.countsByMasterAuthStatus[key] = current - 1;
      }
    },
  },
});

export const {
  setReviewPendingCountByKey,
  setLoadingByKey,
  setError,
  resetReviewPendingCount,
  decrementReviewPendingCount,
  decrementReviewPendingCountByKey,
} = reviewPendingCountSlice.actions;

export interface FetchReviewPendingCountParams {
  org_id: string;
  assigned_to: string;
  /** master_auth_status value (e.g. no_auth_required, auth_on_file). Fetches count for that + agent_order_review_pending. */
  masterAuthStatus: string;
}

/**
 * Thunk: fetches the review-pending count for a given master_auth_status via Algolia.
 * Filters on `agent_order_review_pending` and the specified auth status.
 *
 * @param params - Org ID, assignee, and the master auth status key.
 */
export const fetchReviewPendingCount =
  (params: FetchReviewPendingCountParams) =>
  async (dispatch: any, getState: any) => {
    const { masterAuthStatus } = params;
    const { reviewPendingCount } = getState();
    if (reviewPendingCount.loadingByMasterAuthStatus[masterAuthStatus]) {
      return;
    }
    dispatch(setLoadingByKey({ masterAuthStatus, loading: true }));
    try {
      const requestParams: MultiFacetCountsFromAlgoliaParams = {
        org_id: params.org_id,
        assigned_to: params.assigned_to,
        facet_filters: [
          [`${AlgoliaFacetAttribute.MasterAuthStatus}:${masterAuthStatus}`],
          [
            `${AlgoliaFacetAttribute.MedicalOrderReviewStatus}:${MedicalOrderReviewStatus.AGENT_ORDER_REVIEW_PENDING}`,
          ],
        ],
      };

      const response = await multiFacetCountsFromAlgolia(requestParams);
      const count = response.total_count ?? 0;

      dispatch(setReviewPendingCountByKey({ masterAuthStatus, count }));
    } catch (error) {
      dispatch(setError(error as string));
      dispatch(setReviewPendingCountByKey({ masterAuthStatus, count: 0 }));
    }
  };

export default reviewPendingCountSlice.reducer;
